import React, { useRef, useState } from 'react'
import classes from './PmrEditableDateInput.module.scss'
import { Calendar, CalendarDateTemplateParams, CalendarProps } from 'primereact/calendar'
import { format, isAfter, isBefore, isDate, isWeekend } from 'date-fns'
import clsx from 'clsx'
import moment from 'moment'

interface PmrEditableDateInputProps {
  orderBookedDate?: Date
  hasError?: boolean
  onEditDate(e: Date | null, isInputError: boolean): void
}

const PmrEditableDateInput = (
  props: Omit<CalendarProps, 'onChange' | 'onInput'> & PmrEditableDateInputProps
) => {
  const [hasInternalDateError, setHasInternalDateError] = useState<boolean>(false)
  const calendarRef = useRef<any>(null)

  const checkDateForErrors = (date: Date | null): boolean => {
    let hasError = false

    const isDateValid = date instanceof Date && !isNaN(date.getTime())

    if (!isDate(date) || !isDateValid) hasError = true

    if (date && isDate(date)) {
      const isEarlierThanLatestOrderBookedDate =
        props.orderBookedDate &&
        date <
          new Date(
            props.orderBookedDate.getFullYear(),
            props.orderBookedDate.getMonth(),
            props.orderBookedDate.getDate()
          )

      if (isWeekend(date) || isEarlierThanLatestOrderBookedDate) hasError = true

      if (props.maxDate && isAfter(date, props.maxDate)) hasError = true

      if (props.minDate && isBefore(date, props.minDate)) hasError = true
    }

    setHasInternalDateError(hasError)

    return hasError
  }

  const clearErrors = () => {
    setHasInternalDateError(false)
  }

  const dateTemplate = (e: CalendarDateTemplateParams) => {
    const dateToCompare = new Date(e.year, e.month, e.day)
    const isEarlierThanLatestOrderBookedDate =
      props.orderBookedDate &&
      dateToCompare <
        new Date(
          props.orderBookedDate.getFullYear(),
          props.orderBookedDate.getMonth(),
          props.orderBookedDate.getDate()
        )

    const disabledDates = isEarlierThanLatestOrderBookedDate || isWeekend(dateToCompare)

    return (
      <div
        ref={node => {
          if (node?.parentElement?.parentElement) {
            const isDatePickerToday =
              node.parentElement.parentElement.classList.contains('p-datepicker-today') &&
              !node.parentElement.classList.contains('p-highlight')

            const isDatePickerOtherMonthAndWeekend =
              node.parentElement.parentElement.classList.contains('p-datepicker-other-month') &&
              isWeekend(dateToCompare)

            if (isDatePickerToday)
              node.parentElement.classList.add(classes['remove-today-date-background'])

            if (disabledDates && !isDatePickerOtherMonthAndWeekend) {
              node.parentElement?.classList.add(classes['not-allowed'], 'p-disabled')
              node.parentElement.parentElement.classList.add(classes['pointer-events-disabled'])
            }

            if (isEarlierThanLatestOrderBookedDate) {
              node.parentElement.parentElement.title =
                "Cannot select Dates earlier than the selected Order Lines' Order Booked Date."
            }
          }
        }}
      >
        <span className={clsx({ [classes['not-allowed']]: disabledDates })}>{e.day}</span>
      </div>
    )
  }

  const updateDateView = (updatedDate: Date | null) => {
    if (calendarRef.current) {
      calendarRef.current.setState({
        viewDate: updatedDate,
      })
    }
  }

  const onBlur = () => {
    setHasInternalDateError(false)
  }

  return (
    <Calendar
      {...props}
      ref={calendarRef}
      placeholder="yyyy/mm/dd"
      dateTemplate={dateTemplate}
      onBlur={onBlur}
      onChange={e => {
        // Hack to prevent copy-pasted values to create bubble event, hence, scoping to onClick and Keyboard Events only.
        const originalEvent = e.originalEvent as any
        const targetElement = originalEvent.target as HTMLInputElement
        const trapOnClickEvent =
          originalEvent.nativeEvent && originalEvent.nativeEvent.type === 'click'
        const trapKeyboardEvent = originalEvent.type === 'keypress'
        const trapBackSpaceEvent = originalEvent.type === 'keydown'
        const trapPasteEvents =
          (originalEvent.type === 'input' && originalEvent?.inputType === 'insertFromPaste') ||
          originalEvent.type === 'paste'

        if (trapOnClickEvent || trapKeyboardEvent || trapBackSpaceEvent || trapPasteEvents) {
          try {
            let dateValue = originalEvent.data
              ? moment(originalEvent.data).toDate()
              : e.value
                ? moment(e.value as Date).toDate()
                : null

            dateValue?.setHours(0, 0, 0)

            if (dateValue && trapPasteEvents) format(dateValue, 'yyyy/MM/dd')

            let hasError = false

            if (typeof e.value === 'string' && (e.value as string) === '___/__/__') dateValue = null
            else hasError = checkDateForErrors(dateValue)

            if (!hasError) clearErrors()

            props.onEditDate(dateValue, hasError)
          } catch (err) {
            targetElement.value = ''
            props.onEditDate(null, true)
            updateDateView(null)
            setHasInternalDateError(true)
          }
        }
      }}
      onInput={e => {
        // Hack to force copy-pasting to display actual pasted values onInput Element
        const nativeEvent = e.nativeEvent as any
        const dateValue = nativeEvent.data ? moment(nativeEvent.data).toDate() : null
        dateValue?.setHours(0, 0, 0)

        const targetElement = e.target as HTMLInputElement
        try {
          const hasError = checkDateForErrors(dateValue)
          targetElement.value = dateValue ? format(dateValue, 'yyyy/MM/dd') : ''
          if (!hasError) clearErrors()

          props.onEditDate(dateValue, hasError)
          updateDateView(dateValue)
        } catch (error) {
          targetElement.value = ''
          props.onEditDate(null, true)
          updateDateView(null)
          setHasInternalDateError(true)
        }
      }}
      className={clsx(props.className, {
        [classes['invalid-input']]: hasInternalDateError || props.hasError,
      })}
      panelClassName={`${classes['editable-date-calendar-panel']} ${props.className}`}
      dateFormat="yy/mm/dd"
      monthNavigator={true}
      yearNavigator={true}
      mask="9999/99/99"
      keepInvalid={true}
    />
  )
}
export default PmrEditableDateInput
